import { createFileRoute, Link } from "@tanstack/react-router";
import { Waves, Flame, Trees, Building2, Coins, Users } from "lucide-react";

const URL = "https://whatfinland.com/guides/best-saunas-helsinki";
const TITLE = "Best Public Saunas in Helsinki: A Local's Comparison (2026)";
const DESC =
  "Compare the best public saunas in Helsinki — Löyly, Allas, Kotiharjun, Kulttuurisauna, Uusi Sauna. Price, vibe, wood-fired vs electric, sea pools and how to choose.";
const COVER = "https://whatfinland.com/og/helsinki-saunas.jpg";

type Sauna = {
  name: string;
  area: string;
  vibe: string;
  type: "Wood-fired" | "Electric" | "Smoke" | "Mixed";
  price: string;
  facilities: string;
  highlight: string;
  link?: { to: string; label: string };
};

const SAUNAS: Sauna[] = [
  {
    name: "Löyly",
    area: "Hernesaari, south shore",
    vibe: "Architect-designed, modern, design-crowd",
    type: "Wood-fired",
    price: "€21 (2h)",
    facilities: "Sea dip, restaurant, terrace bar",
    highlight: "Iconic sculptural wood facade; sunset slot is the one to book.",
  },
  {
    name: "Allas Sea Pool",
    area: "Market Square, harbour",
    vibe: "Touristy but unbeatable view of the cathedral",
    type: "Electric",
    price: "€18 (day)",
    facilities: "Heated sea pool, café, terrace",
    highlight: "Heated Baltic pool means you can swim year-round without nerve.",
  },
  {
    name: "Kotiharjun Sauna",
    area: "Kallio",
    vibe: "Old-school working-class, no frills, locals",
    type: "Wood-fired",
    price: "€16",
    facilities: "Street cool-down with beer, scrub services",
    highlight: "Helsinki's last traditional public wood-fired sauna since 1928.",
  },
  {
    name: "Kulttuurisauna",
    area: "Hakaniemi waterfront",
    vibe: "Minimal, quiet, design-led, adults-only",
    type: "Wood-fired",
    price: "€20",
    facilities: "Sea dip, cold shower, no alcohol",
    highlight: "Phone-free, contemplative — the antidote to Löyly's crowd.",
  },
  {
    name: "Uusi Sauna",
    area: "Jätkäsaari",
    vibe: "Neighbourhood bar-meets-sauna, social",
    type: "Wood-fired",
    price: "€19",
    facilities: "Bar, food, mixed/private slots",
    highlight: "Pair sauna with a long evening of beer and pizza — very Helsinki.",
  },
];

export const Route = createFileRoute("/guides/best-saunas-helsinki")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "best sauna helsinki, public sauna helsinki, löyly helsinki, allas sea pool, kotiharjun sauna, helsinki sauna culture" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: COVER },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: COVER },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          image: [COVER],
          datePublished: "2026-06-11",
          dateModified: "2026-06-11",
          author: { "@type": "Organization", name: "what Finland" },
          publisher: {
            "@type": "Organization",
            name: "what Finland",
            logo: { "@type": "ImageObject", url: "https://whatfinland.com/icon-512.png" },
          },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "what Finland", item: "https://whatfinland.com/" },
            { "@type": "ListItem", position: 2, name: "Guides", item: "https://whatfinland.com/" },
            { "@type": "ListItem", position: 3, name: "Best Saunas in Helsinki", item: URL },
          ],
        }),
      },
    ],

  }),
  component: SaunaGuide,
});

function SaunaGuide() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:py-16">
      <header className="mb-10">
        <p className="text-sm uppercase tracking-wider text-navy/60 mb-3">Helsinki Guide</p>
        <h1 className="font-heading text-3xl sm:text-5xl text-navy leading-tight mb-4">
          Best Public Saunas in Helsinki
        </h1>
        <p className="text-lg text-navy/80 leading-relaxed">
          Five public saunas worth the trip — wood-fired, modern, traditional and waterfront —
          compared on price, vibe and facilities so you pick the right one for your evening.
        </p>
      </header>

      <section className="mb-10 rounded-2xl bg-navy/5 p-5 sm:p-6">
        <h2 className="font-heading text-xl text-navy mb-3 flex items-center gap-2">
          <Users className="size-5" /> How to choose
        </h2>
        <ul className="space-y-2 text-navy/85">
          <li><strong>First time in Helsinki?</strong> Allas for the view, or Löyly for the architecture.</li>
          <li><strong>Want the real thing?</strong> Kotiharjun — wood-fired, no English, no frills.</li>
          <li><strong>Want quiet?</strong> Kulttuurisauna — phone-free, no alcohol, sea dip.</li>
          <li><strong>Going with a group?</strong> Uusi Sauna — bar, food, social.</li>
        </ul>
      </section>

      <section className="space-y-5">
        {SAUNAS.map((s) => (
          <article key={s.name} className="rounded-2xl border border-navy/10 p-5 sm:p-6 bg-white">
            <header className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-2xl text-navy">{s.name}</h3>
                <p className="text-sm text-navy/60">{s.area}</p>
              </div>
              <span className="shrink-0 rounded-full bg-navy/10 px-3 py-1 text-xs text-navy">
                {s.type}
              </span>
            </header>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-3">
              <Stat icon={Coins} label="Price" value={s.price} />
              <Stat icon={Flame} label="Vibe" value={s.vibe} />
              <Stat icon={Waves} label="Facilities" value={s.facilities} />
            </dl>
            <p className="text-navy/85 leading-relaxed">{s.highlight}</p>
          </article>
        ))}
      </section>

      <section className="mt-12 mb-10">
        <h2 className="font-heading text-2xl text-navy mb-4">Official Sauna Websites</h2>
        <p className="text-navy/70 text-sm mb-3">Book directly with each sauna — opening hours and slots change weekly.</p>
        <ul className="space-y-2 text-sm text-navy/80">
          <li>• <a href="https://www.loylyhelsinki.fi/" target="_blank" rel="noopener" className="underline hover:text-navy">Löyly Helsinki</a> — Hernesaari, wood-fired, sea dip</li>
          <li>• <a href="https://www.allasseapool.fi/en/" target="_blank" rel="noopener" className="underline hover:text-navy">Allas Sea Pool</a> — Market Square, heated sea pool</li>
          <li>• <a href="https://www.kotiharjunsauna.fi/" target="_blank" rel="noopener" className="underline hover:text-navy">Kotiharjun Sauna</a> — Kallio, traditional 1928 wood-fired</li>
          <li>• <a href="https://www.kulttuurisauna.fi/" target="_blank" rel="noopener" className="underline hover:text-navy">Kulttuurisauna</a> — Hakaniemi waterfront, phone-free</li>
          <li>• <a href="https://uusisauna.fi/en/" target="_blank" rel="noopener" className="underline hover:text-navy">Uusi Sauna</a> — Jätkäsaari, bar & sauna</li>
        </ul>
        <p className="text-navy/60 text-xs mt-4">
          Background reading: <a href="https://www.visitfinland.com/en/articles/finnish-sauna-culture/" target="_blank" rel="noopener" className="underline">Finnish sauna culture (Visit Finland)</a> and <a href="https://ich.unesco.org/en/RL/sauna-culture-in-finland-01596" target="_blank" rel="noopener" className="underline">UNESCO listing for Finnish sauna culture</a>.
        </p>
      </section>

      <section className="mt-12 rounded-2xl bg-navy text-white p-6 sm:p-8">
        <h2 className="font-heading text-2xl mb-3 flex items-center gap-2">
          <Trees className="size-6" /> Plan the rest of your trip
        </h2>
        <p className="text-white/85 mb-4">
          Pair your sauna evening with dinner in Punavuori, a walk through the Design District, or
          a ferry to Suomenlinna. Our 48-hour itinerary stitches it all together.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/guides/48-hours-in-helsinki" className="rounded-full bg-white text-navy px-5 py-2 font-medium">
            48 Hours in Helsinki
          </Link>
          <Link to="/do" className="rounded-full border border-white/40 px-5 py-2 font-medium">
            All Things to Do
          </Link>
          <Link to="/eat" className="rounded-full border border-white/40 px-5 py-2 font-medium">
            Where to Eat
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="rounded-xl bg-navy/5 p-3">
      <div className="flex items-center gap-2 text-navy/60 text-xs mb-1">
        <Icon className="size-4" />
        {label}
      </div>
      <div className="text-navy font-medium leading-snug">{value}</div>
    </div>
  );
}

// Suppress unused-import lint for icons reserved for future expansion
void Building2;
